/**
 * The dashboard's shared 1-second clock.
 *
 * SEPARATE FROM THE LIVE DATA STORE, deliberately.
 *
 * Timers and agent/call data change at completely different rates. A call's
 * elapsed time changes every second by definition; the agent list changes when
 * somebody actually answers, pauses or logs out — which on a real floor is
 * every several seconds at most.
 *
 * When the two shared one snapshot, every tick republished the whole agent and
 * call arrays (`toAgentSummaries` builds fresh objects each call, so identity
 * changed even when every field was identical), which re-rendered entire tables
 * — dozens of rows, hundreds of cells — to move a handful of `0:42`s to `0:43`.
 *
 * Split, the cost collapses: a tick re-renders only the individual `<Elapsed>`
 * spans that subscribe to it, and the rows around them stay untouched.
 *
 * One module-level interval serves every subscriber, and it only runs while
 * something is actually subscribed — a dashboard left open on another tab does
 * not keep a timer alive for nobody.
 */
import { useSyncExternalStore } from 'react'

const TICK_MS = 1000

let now = Date.now()
let timer: ReturnType<typeof setInterval> | null = null
const listeners = new Set<() => void>()

function start(): void {
  if (timer !== null) return
  timer = setInterval(() => {
    now = Date.now()
    for (const listener of listeners) listener()
  }, TICK_MS)
}

function stop(): void {
  if (timer === null) return
  clearInterval(timer)
  timer = null
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  start()
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) stop()
  }
}

function getSnapshot(): number {
  return now
}

/**
 * The current time, refreshed once a second.
 *
 * Every subscriber receives the SAME value within a tick, so two timers on
 * screen can never disagree — which they could if each cell called `Date.now()`
 * during its own render.
 *
 * Use this only in leaf components that display a duration. Calling it higher
 * up re-renders that whole subtree every second, which is exactly the cost this
 * module exists to avoid.
 */
export function useNow(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/** Test seam. */
export function __resetClock(): void {
  stop()
  listeners.clear()
  now = Date.now()
}
